/**
 * Pure planning for copying and moving meal logs.
 *
 * Mirrors `WorkoutCloner` (`lib/local-api/workout/workout-clone.ts`): all the
 * gnarly parts — slot/day preservation, null handling, move semantics, revert —
 * live here as static methods over injected inputs (`now`, `newId`), with no
 * `db`, no clock and no uuid import. That is what makes them testable in the
 * `unit` jest project, which has no `@/` module mapper and no `jest.mock`.
 *
 * **Copy INSERTs new rows. Move UPDATEs existing rows in place.** They are not
 * the same operation with a delete bolted on: a `MealLog`'s `id` is referenced
 * from outside the meal screens (the AI chat's structured meal card resolves a
 * log by id), so delete-plus-insert would silently orphan a user's chat history
 * on exactly the path the requirements call the *safe* correction.
 */
import type { MealLogRecord } from '~/powersync/generated/schema';
import { ConsumedDateRange } from './consumed-date-range';

/**
 * Explicit sentinel, because `null` is a MEANINGFUL `mealType` (untyped) and
 * would otherwise be ambiguous with "keep the source's own". Conflating the two
 * collapses every copied row to untyped.
 */
export const KEEP_ORIGINAL = Symbol('keep-original');
export type KeepOriginal = typeof KEEP_ORIGINAL;

export interface MealLogCopyDestination {
  /**
   * Destination local calendar day, `"yyyy-MM-dd"`. Always present — the picker
   * defaults to the source day, which writes the source `consumedAt` back
   * verbatim, so no separate "keep the day" control is needed.
   */
  day: string;
  /** The destination slot, `null` to set untyped, or `KEEP_ORIGINAL` to preserve each source's own. */
  mealType: string | null | KeepOriginal;
}

export type MealLogCopyMode = 'copy' | 'move';

/** The only two fields a move ever writes. Doubles as the undo record. */
export interface MealLogPlacement {
  id: string;
  consumedAt: string;
  mealType: string | null;
}

export interface MealLogCopyPlan {
  /** COPY only — new rows, built as `MealLogMutations.create` builds one. */
  inserts: MealLogRecord[];
  /** MOVE only — in-place placement updates on existing rows. */
  updates: MealLogPlacement[];
  /** COPY-undo only — ids to hard-delete. Empty for a move. */
  deleteIds: string[];
}

/** Per destination day+slot, how many items landed — drives the summary lines. */
export interface MealLogCopyGroup {
  day: string;
  mealType: string | null;
  count: number;
  /**
   * The meals that landed in this group, in order, DUPLICATES KEPT — two
   * helpings are two named items, not one.
   *
   * Purely for the summary's OPTIONAL name enrichment: the modal maps these
   * through the `mealNamesById` map the calling screen already holds, and
   * renders nothing for an id it cannot name (`Meal` is not synced, so names
   * come from GraphQL and may be absent — INV-0013). The count lines never
   * depend on this.
   */
  mealIds: string[];
}

/** What `summarise` needs off a landed row — a placement plus the meal it names. */
export type MealLogSummaryRow = MealLogPlacement & { mealId?: string | null };

export interface MealLogCopyResult {
  mode: MealLogCopyMode;
  destination: MealLogCopyDestination;
  /** COPY — the rows created, for undo. */
  insertedIds: string[];
  /** MOVE — each moved row's placement BEFORE the move, for undo. */
  priorStates: MealLogPlacement[];
  groups: MealLogCopyGroup[];
}

export class MealLogCopyPlanner {
  /**
   * Where a source row lands.
   *
   * **The placement rule: change the day, never the time-of-day.** A move is a
   * correction to *which day/slot* an entry belongs to — not a claim about *when*
   * the user ate. So the destination date replaces only the calendar-date part,
   * and whatever the source recorded about time is carried across.
   */
  private static placementFor(
    source: MealLogRecord,
    destination: MealLogCopyDestination,
  ): MealLogPlacement {
    return {
      id: source.id,
      consumedAt: MealLogCopyPlanner.consumedAtFor(
        source.consumedAt as string,
        destination.day,
      ),
      mealType:
        destination.mealType === KEEP_ORIGINAL
          ? ((source.mealType as string | null) ?? null)
          : destination.mealType,
    };
  }

  /**
   * The three-case placement rule (DD2). Which of `consumedAt`'s three stored
   * shapes the source is decides what "its time" means, classified by the SAME
   * authority the day filtering uses (`ConsumedDateRange.toCalendarDate`), so
   * there is one definition of shape in the codebase.
   */
  private static consumedAtFor(
    consumedAt: string,
    destinationDay: string,
  ): string {
    const sourceDayKey = ConsumedDateRange.toDayKey(
      ConsumedDateRange.toCalendarDate(consumedAt),
    );

    // Case 1 — the destination IS the source's own local day. Checked first, and
    // it wins over the two cases below: re-deriving would rewrite a shape-2
    // "…T00:00:00.000Z" into a bare "2026-03-12" — a different string for the
    // same instant, which breaks byte-identity and turns a same-day/same-slot
    // move into a spurious PowerSync PUT instead of a true no-op.
    if (sourceDayKey === destinationDay) {
      return consumedAt;
    }

    // Case 2 — a date-only value (bare date, or the exact-UTC-midnight
    // round-trip). No meaningful time-of-day, so write the destination day as a
    // bare local date. NEVER invent a time.
    if (ConsumedDateRange.isDateOnly(consumedAt)) {
      return destinationDay;
    }

    // Case 3 — a real instant. Take its LOCAL time-of-day and recombine it with
    // the destination's local calendar date.
    //
    // DST caveat: if the destination is a spring-forward day and the source time
    // falls in the skipped hour, `new Date(y, m, d, 2, 30)` normalises to 03:30;
    // on fall-back an ambiguous local time resolves to one of the two offsets,
    // unspecified. The result always stays on the correct local calendar day, so
    // no row lands on the wrong day — the exposure is a silent one-hour drift,
    // twice a year.
    const instant = new Date(consumedAt);
    const [year, month, day] = destinationDay.split('-').map(Number);
    return new Date(
      year,
      month - 1,
      day,
      instant.getHours(),
      instant.getMinutes(),
      instant.getSeconds(),
      instant.getMilliseconds(),
    ).toISOString();
  }

  /**
   * Plans a copy or a move.
   *
   * @param now    ISO timestamp stamped on created rows — injected, so this stays pure.
   * @param newId  Mints a fresh uuid — injected for the same reason.
   */
  static plan(
    sources: MealLogRecord[],
    destination: MealLogCopyDestination,
    mode: MealLogCopyMode,
    now: string,
    newId: () => string,
  ): MealLogCopyPlan {
    const placements = sources.map((source) =>
      MealLogCopyPlanner.placementFor(source, destination),
    );

    if (mode === 'move') {
      // Nothing is created and nothing is deleted: the rows keep their `id` and
      // `niceId`, which is simply what "move" means — and what keeps an AI chat
      // card pointing at a moved log resolving.
      return { inserts: [], updates: placements, deleteIds: [] };
    }

    // A copy builds each row exactly as `MealLogMutations.create` builds one,
    // NEVER by cloning the source record — see the field table in DD5.
    const inserts = sources.map((source, index): MealLogRecord => {
      const placement = placements[index];
      return {
        ...source,
        id: newId(),
        // INV-0010: the sync rules own userId; PowerSyncResolver overwrites it
        // server-side from the authenticated token.
        userId: '',
        // The PowerSync CRUD trigger strips nulls, so this never reaches the
        // wire and the server's generate_meal_log_nice_id() default fires.
        niceId: null,
        consumedAt: placement.consumedAt,
        mealType: placement.mealType,
        createdAt: now,
        updatedAt: now,
      } as MealLogRecord;
    });

    return { inserts, updates: [], deleteIds: [] };
  }

  /**
   * The inverse of an applied operation.
   *
   * A copy undoes by deleting exactly the ids it inserted; a move undoes by
   * re-applying the captured prior placements. Needs neither a clock nor an id
   * factory — there is nothing new to create.
   */
  static planRevert(result: MealLogCopyResult): MealLogCopyPlan {
    if (result.mode === 'move') {
      return { inserts: [], updates: result.priorStates, deleteIds: [] };
    }
    return { inserts: [], updates: [], deleteIds: result.insertedIds };
  }

  /**
   * The per-day+slot counts the summary renders.
   *
   * **Groups by (local calendar day, slot), never by the raw `consumedAt`.**
   * Under the placement rule, rows landing on one day carry distinct instants
   * (verbatim writeback, and preserved times-of-day across a move), so keying on
   * the instant would emit one line per row.
   */
  static summarise(rows: MealLogSummaryRow[]): MealLogCopyGroup[] {
    const groups: MealLogCopyGroup[] = [];
    const index = new Map<string, MealLogCopyGroup>();

    for (const row of rows) {
      const dayKey = ConsumedDateRange.toDayKey(
        ConsumedDateRange.toCalendarDate(row.consumedAt),
      );
      const key = `${dayKey}::${row.mealType ?? ''}`;

      const existing = index.get(key);
      if (existing) {
        existing.count += 1;
        if (row.mealId != null) existing.mealIds.push(row.mealId);
        continue;
      }
      const group: MealLogCopyGroup = {
        day: dayKey,
        mealType: row.mealType,
        count: 1,
        mealIds: row.mealId != null ? [row.mealId] : [],
      };
      index.set(key, group);
      groups.push(group);
    }

    return groups;
  }
}
